import React from 'react'
import { observer } from 'mobx-react-lite'

import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'
import { type SxProps } from '@mui/material/styles'

import { H3, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore } from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { type Recurrence } from '../../core'

import { groupRecurrencesByDate } from '../../core/utils/RecurringTransactionsUtil'

import { RecurrenceRow } from './RecurrenceRow'

interface RecurrenceListProps {
  recurrences: Recurrence[]
  isMini?: boolean
  onResetClick?: () => void
  onRecurrenceClick?: (recurrence: Recurrence) => void
  sx?: SxProps
  title?: string
}

export const RecurrenceList = observer(
  ({
    recurrences,
    isMini = false,
    onResetClick,
    onRecurrenceClick,
    sx = {},
    title,
  }: RecurrenceListProps) => {
    const { recurring: copy } = useGlobalCopyStore()

    const groupedRecurrences = React.useMemo(() => {
      return groupRecurrencesByDate(recurrences)
    }, [recurrences])

    return (
      <Stack id="recurrence-list" sx={{ gap: 8, mt: 4, ...sx }} tabIndex={-1}>
        {title && (
          <Stack
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'space-between',
              px: 24,
            }}
          >
            <H3 bold={true} variant="body1">
              {title}
            </H3>
            {onResetClick && (
              <Button onClick={onResetClick} size="small" variant="text">
                {copy.reset}
              </Button>
            )}
          </Stack>
        )}

        {recurrences.length === 0 ? (
          <Text fontStyle="italic" sx={{ py: 24, textAlign: 'center' }}>
            {copy.no_activities}
          </Text>
        ) : (
          <List aria-label={title}>
            {isMini
              ? recurrences.map((recurrence, index) => (
                  <React.Fragment key={`recurrence-${index}`}>
                    <RecurrenceRow
                      isMini={isMini}
                      onRecurrenceClick={onRecurrenceClick}
                      recurrence={recurrence}
                    />
                    <Divider sx={{ mx: 24 }} />
                  </React.Fragment>
                ))
              : groupedRecurrences.map((group, index) => (
                  <React.Fragment key={`recurrence-${index}`}>
                    <ListItem key={group.id}>
                      <List
                        aria-labelledby={`date-group-header-${group.id}`}
                        subheader={
                          <ListSubheader
                            aria-hidden="true"
                            id={`date-group-header-${group.id}`}
                            role="presentation"
                            sx={{ bgcolor: 'background.paper', ml: 8, pb: 4, pt: 12 }}
                          >
                            <Text bold={true} variant="caption">
                              {formatDate(group.date, DATE_FORMATS_INTL.DAY_OF_WEEK_MONTH_DAY)}
                            </Text>
                          </ListSubheader>
                        }
                        sx={{ width: '100%' }}
                      >
                        {group.recurrences.map((recurrence, index) => (
                          <React.Fragment key={`day-recurrence-${index}`}>
                            <RecurrenceRow
                              isMini={isMini}
                              onRecurrenceClick={onRecurrenceClick}
                              recurrence={recurrence}
                            />
                          </React.Fragment>
                        ))}
                      </List>
                    </ListItem>
                    <Divider sx={{ mx: 24 }} />
                  </React.Fragment>
                ))}
          </List>
        )}
      </Stack>
    )
  },
)
